import React from 'react'
import { LoaderBar, AppEditButton, AppRemoveButtonPopup } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { DateFormat, DateUtility } from 'utility'
import styled from 'styled-components'

const Frame = styled.div`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    overflow-x: auto;
`

const Grid = styled.table`
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
    table-layout: fixed;

    th {
        text-align: left;
        padding: 12px 14px;
        background: #fafaf9;
        color: #78716c;
        font-size: 12px;
        font-weight: 600;
        border-bottom: 1px solid #e7e5e4;
        white-space: nowrap;
    }

    td {
        padding: 12px 14px;
        border-bottom: 1px solid #f5f5f4;
        color: #1c1917;
        font-size: 14px;
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    tbody tr:hover td {
        background: #fafaf9;
    }

    th:nth-child(1),
    td:nth-child(1) {
        width: 28%;
    }

    ${props =>
        props.$actions
            ? `
        th:last-child,
        td:last-child {
            width: 112px;
            text-align: right;
            white-space: nowrap;
        }
    `
            : ''}
`

const Title = styled.div`
    font-weight: ${props => (props.$read ? 500 : 700)};
    line-height: 1.3;
`

const Pill = styled.button`
    border: 1px solid transparent;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
    background: ${props => props.$bg};
    color: ${props => props.$color};
    border-color: ${props => props.$border};
`

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;

    .ui.button {
        margin: 0 !important;
        padding: 0.55em !important;
        min-width: 32px !important;
        width: 32px;
        height: 32px;
    }
`

const Card = styled.article`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    padding: 14px;
    margin-bottom: 10px;
`

const CardTop = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
`

const Meta = styled.div`
    margin-top: 8px;
    color: #78716c;
    font-size: 12.5px;
    line-height: 1.4;
`

const RowPills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
`

const Empty = styled.div`
    padding: 36px 16px;
    text-align: center;
    color: #78716c;
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
`

const formatDateTime = dateTimeString => {
    if (!dateTimeString) return '—'
    try {
        const date = new Date(dateTimeString)
        if (Number.isNaN(date.getTime())) return dateTimeString
        return DateUtility.dateToLocalISO(date, DateFormat.dateTime)
    } catch {
        return dateTimeString
    }
}

const shown = value => {
    const raw = value == null ? '' : String(value).trim()
    return raw || '—'
}

const priorityOf = priority => {
    if (priority === 3) return { text: 'High', bg: '#fee2e2', color: '#991b1b', border: '#fecaca' }
    if (priority === 2) return { text: 'Medium', bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' }
    return { text: 'Normal', bg: '#fef9c3', color: '#854d0e', border: '#fde68a' }
}

const PriorityPill = ({ priority }) => {
    const tone = priorityOf(priority)
    return (
        <Pill type="button" $bg={tone.bg} $color={tone.color} $border={tone.border}>
            {tone.text}
        </Pill>
    )
}

const StatusPill = ({ item, onMarkAsRead }) => (
    <Pill
        type="button"
        $clickable={!item.IsRead}
        $bg={item.IsRead ? '#f0fdf4' : '#eff6ff'}
        $color={item.IsRead ? '#166534' : '#1d4ed8'}
        $border={item.IsRead ? '#bbf7d0' : '#bfdbfe'}
        onClick={() => {
            if (!item.IsRead) onMarkAsRead(item.AnnouncementId)
        }}
    >
        {item.IsRead ? 'Read' : 'Unread'}
    </Pill>
)

const RowActions = ({ item, onEdit, onDeactivate }) => (
    <Actions>
        <AppEditButton onClick={() => onEdit(item)} />
        <AppRemoveButtonPopup onConfirm={() => onDeactivate(item.AnnouncementId)} />
    </Actions>
)

const AnnouncementCard = ({ item, onEdit, onDeactivate, onMarkAsRead, isSuperAdmin }) => (
    <Card>
        <CardTop>
            <div>
                <Title $read={item.IsRead}>{shown(item.Title)}</Title>
            </div>
            {isSuperAdmin && (
                <RowActions item={item} onEdit={onEdit} onDeactivate={onDeactivate} />
            )}
        </CardTop>
        <Meta>
            {formatDateTime(item.CreatedOn)}
            {shown(item.CreatedByName) !== '—' ? ` · ${shown(item.CreatedByName)}` : ''}
        </Meta>
        <RowPills>
            <PriorityPill priority={item.Priority} />
            <StatusPill item={item} onMarkAsRead={onMarkAsRead} />
        </RowPills>
    </Card>
)

export const AnnouncementList = ({ data, loading, onEdit, onDeactivate, onMarkAsRead, isSuperAdmin }) => {
    if (loading) return <LoaderBar content="Loading announcements..." />
    const rows = Array.isArray(data) ? data : []
    if (rows.length === 0) return <Empty>No announcements yet.</Empty>

    return (
        <>
            <DesktopMode>
                <Frame>
                    <Grid $actions={isSuperAdmin}>
                        <thead>
                            <tr>
                                <th>Announcement</th>
                                <th>Created on</th>
                                <th>Created by</th>
                                <th>Priority</th>
                                <th>Status</th>
                                {isSuperAdmin && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(item => (
                                <tr key={item.AnnouncementId}>
                                    <td>
                                        <Title $read={item.IsRead}>{shown(item.Title)}</Title>
                                    </td>
                                    <td>{formatDateTime(item.CreatedOn)}</td>
                                    <td>{shown(item.CreatedByName)}</td>
                                    <td><PriorityPill priority={item.Priority} /></td>
                                    <td><StatusPill item={item} onMarkAsRead={onMarkAsRead} /></td>
                                    {isSuperAdmin && (
                                        <td>
                                            <RowActions
                                                item={item}
                                                onEdit={onEdit}
                                                onDeactivate={onDeactivate}
                                            />
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Grid>
                </Frame>
            </DesktopMode>
            <MobileMode>
                {rows.map(item => (
                    <AnnouncementCard
                        key={item.AnnouncementId}
                        item={item}
                        onEdit={onEdit}
                        onDeactivate={onDeactivate}
                        onMarkAsRead={onMarkAsRead}
                        isSuperAdmin={isSuperAdmin}
                    />
                ))}
            </MobileMode>
        </>
    )
}
